import React, { useEffect, useState } from 'react';
import {
	ActionMenu,
	ActionMenuContext,
	BodyText,
	Button,
	Card,
	Icon,
	Tooltip,
	Modal,
	Drawer,
	Dialog,
	Takeover,
	Popover,
	useStorybookFigmaAddon,
	Stack,
} from '../../system';

export default {
	title: 'Components/ActionMenu',
	parameters: {
		...useStorybookFigmaAddon('32%3A1082'),
	},
	includeStories: [
		'actionMenu',
		'testContextMenu',
		'testOverlayComponents',
		'testActionMenuOverflow',
		'TestingMultipleActionMenu',
		'Tester',
	],
};

const log = () => console.log('item clicked, closing');

export const Tester = () => {
	return (
		<ActionMenu
			data-tester="test"
			trigger={({ toggle }) => (
				<Button onClick={toggle}>Open Menu</Button>
			)}
		>
			<ActionMenu
				direction="rb"
				trigger={({ toggle }) => (
					<ActionMenu.Item cascading onClick={toggle}>
						Menu 01
					</ActionMenu.Item>
				)}
			>
				<ActionMenu.Item>Nested Menu 01</ActionMenu.Item>
				<ActionMenu.Item>Nested Menu 02</ActionMenu.Item>
			</ActionMenu>
			<ActionMenu.Item>Menu 02</ActionMenu.Item>
		</ActionMenu>
	);
};
export const Menu = (props: any) => {
	const {
		direction,
		onClose,
		onOpen,
		trigger,
		portal = true,
		...rest
	} = props;
	const [open, setOpen] = React.useState(rest.open || false);
	const parentContext = React.useContext(ActionMenuContext)?.slice(-1)[0];
	const parentMenuContainer = parentContext?.itemsContainerRef?.current;
	const handleClickOutside = (e: any) => {
		if (portal) {
			setOpen(false); // Close current menu
			if (e.key === 'ArrowLeft') return; // Close only one menu
			if (parentMenuContainer?.contains(e.target)) return;
			parentContext?.onClickOutside?.(e); // Close higher level menus
		} else {
			if (parentMenuContainer?.contains(e.target)) {
				const nestedMenus = Array.from(
					parentMenuContainer?.querySelectorAll(
						'[data-anvil-component=ActionMenu]'
					)
				);
				// Exclude event on non-portaled submenu
				if (!nestedMenus.some((el: any) => el.contains(e.target)))
					return;
				setOpen(false); // Close current menu
				if (e.key === 'ArrowLeft') return; // Close only one menu
				parentContext?.onClickOutside?.(e); // Close higher level menus
			}
		}
	};

	useEffect(() => {
		open ? onOpen?.() : onClose?.();
	}, [open]); // eslint-disable-line

	return (
		<ActionMenu
			{...rest}
			direction={direction || 'rb'}
			open={open}
			onClickOutside={handleClickOutside}
			portal={portal}
			trigger={trigger?.({ onClick: () => setOpen(!open) })}
		/>
	);
};

export const MenuItem = (props: any) => {
	const parentContext = React.useContext(ActionMenuContext)?.slice(-1)[0];
	const handleClick = (e: any) => {
		props.onClick?.(e);
		if (props.cascading) return;
		parentContext?.onClickOutside?.(e);
	};
	const handleExit = (e: React.KeyboardEvent) => {
		props.onKeyDown?.(e);
		if (e.key === 'Escape' || e.key === 'Tab') {
			parentContext?.onClickOutside?.(e as any); // Close all the nested Menus
			if (e.shiftKey && e.key === 'Tab') e.preventDefault(); // To keep focus on trigger
		}
	};

	return (
		<ActionMenu.Item
			{...props}
			onClick={handleClick}
			onKeyDown={handleExit}
		/>
	);
};

const MenuItems = () => (
	<>
		<MenuItem onClick={log}>Item</MenuItem>
		<MenuItem disabled>Disabled</MenuItem>
		<Menu
			direction="rb"
			trigger={(p: any) => (
				<MenuItem {...p} cascading>
					Cascading
				</MenuItem>
			)}
		>
			<ContactItems />
		</Menu>
		<ActionMenu.Group>
			<MenuItem disabled>Disabled</MenuItem>
			<MenuItem onClick={log} icon="help">
				Icon
			</MenuItem>
			<MenuItem onClick={log} icon="help" disabled>
				Icon Disabled
			</MenuItem>
		</ActionMenu.Group>
		<ActionMenu.Group label="Group with Label">
			<MenuItem onClick={log} icon="help">
				Icon
			</MenuItem>
			<Menu
				direction="rb"
				trigger={(p: any) => (
					<MenuItem {...p} cascading icon="help" iconColor="red">
						Colored Icon Cascading Submenu
					</MenuItem>
				)}
			>
				<ContactItems />
			</Menu>
			<MenuItem onClick={log}>
				<BodyText size="medium" className="m-b-0-i">
					Download XLSX
				</BodyText>
			</MenuItem>
		</ActionMenu.Group>
	</>
);

const ContactItems = () => (
	<>
		<ActionMenu.Group label="Text">
			<MenuItem onClick={log} icon="phone_android">
				<BodyText subdued size="xsmall">
					Monica’s cell
				</BodyText>
				(555) 123-4567
			</MenuItem>
		</ActionMenu.Group>
		<ActionMenu.Group label="Call">
			<MenuItem onClick={log} icon="phone">
				<BodyText subdued size="xsmall">
					Home
				</BodyText>
				(555) 123-4567
			</MenuItem>
			<MenuItem onClick={log} icon="phone_android">
				<BodyText subdued size="xsmall">
					Monica’s cell
				</BodyText>
				(555) 123-4567
			</MenuItem>
		</ActionMenu.Group>
		<ActionMenu.Group label="Email">
			<MenuItem onClick={log} icon="email">
				<BodyText subdued size="xsmall">
					Monica's personal
				</BodyText>
				monicadoe@gmail.com
			</MenuItem>
			<ActionMenu.Group>
				<Menu
					direction="rb"
					trigger={(p: any) => (
						<MenuItem {...p} cascading>
							One more menu
						</MenuItem>
					)}
				>
					<MenuItem onClick={log}>Item 1</MenuItem>
					<MenuItem onClick={log}>Item 2</MenuItem>
					<MenuItem onClick={log}>Item 3</MenuItem>
				</Menu>
				<Menu
					direction="rb"
					trigger={(p: any) => (
						<MenuItem {...p} cascading>
							Another menu
						</MenuItem>
					)}
				>
					<MenuItem onClick={log}>Item 4</MenuItem>
					<MenuItem onClick={log}>Item 5</MenuItem>
					<MenuItem onClick={log}>Item 6</MenuItem>
				</Menu>
			</ActionMenu.Group>
		</ActionMenu.Group>
	</>
);

export const actionMenu = (args: any) => {
	const [open, setOpen] = useState(true);
	const handleClick = () => setOpen(!open);
	const handleClickOutside = () => setOpen(false);

	const [key, setKey]: [number, Function] = React.useState(0);
	const handleRefresh = () => setKey(key + 1);

	return (
		<div key={key} className="p-4 ta-center">
			{loremIpsum}
			<ActionMenu
				{...args}
				className="classname-test"
				wrapperClassName="wrapper-classname-test"
				trigger={(props: any) => (
					<Button {...props} onClick={handleClick}>
						Action Menu
						<Icon name="more_vert" size="24" className="m-l-2" />
					</Button>
				)}
				open={open}
				onClickOutside={handleClickOutside}
				onOpen={() => console.log('onOpen fired')}
				onClose={() => console.log('onClose fired')}
			>
				<ActionMenu.Item onClick={log}>Item</ActionMenu.Item>
				<ActionMenu.Item disabled>Disabled</ActionMenu.Item>
				<Menu
					direction="rb"
					trigger={(p: any) => (
						<MenuItem {...p} cascading>
							Cascading
						</MenuItem>
					)}
				>
					<ContactItems />
				</Menu>
				<ActionMenu.Group>
					<MenuItem disabled>Disabled</MenuItem>
					<MenuItem onClick={log} icon="help">
						Icon
					</MenuItem>
					<MenuItem onClick={log} icon="help" disabled>
						Icon Disabled
					</MenuItem>
				</ActionMenu.Group>
				<ActionMenu.Group label="Group with Label">
					<MenuItem onClick={log} icon="help">
						Icon
					</MenuItem>
					<Menu
						direction="rb"
						trigger={(p: any) => (
							<MenuItem
								{...p}
								cascading
								icon="help"
								iconColor="red"
							>
								Colored Icon Cascading Submenu
							</MenuItem>
						)}
					>
						<ContactItems />
					</Menu>
					<MenuItem onClick={log}>
						<BodyText size="medium" className="m-b-0-i">
							Download XLSX
						</BodyText>
					</MenuItem>
				</ActionMenu.Group>
			</ActionMenu>
			<ActionMenu
				{...args}
				className="classname-test"
				wrapperClassName="wrapper-classname-test"
				popoverContentClassName="ActionMenu" // This is only added to fix SB issue
				trigger={({ isOpen, toggle }) => (
					<Button
						className="m-l-4"
						onClick={toggle}
						selected={isOpen}
					>
						Action Menu Uncontrolled
						<Icon name="more_vert" size="24" className="m-l-2" />
					</Button>
				)}
			>
				<MenuItems />
			</ActionMenu>
			{loremIpsum}
			<div className="m-x-8 ta-left">
				<Button onClick={handleRefresh}>Refresh</Button>
			</div>
		</div>
	);
};
actionMenu.args = {
	sharp: false,
	width: 's',
	direction: 'bl',
};

export const testContextMenu = (args: any) => {
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });

	const close = () => setOpen(false);
	const handleEscape = (e: any) =>
		['Tab', 'Escape'].includes(e.key) && close();

	const handleContextMenu = (e: any) => {
		e.preventDefault();
		e.persist();
		setOpen(true);
		setPosition({
			x: e.pageX,
			y: e.pageY,
		});
	};

	const styles: React.CSSProperties = {
		position: 'absolute',
		width: 0,
		height: 0,
		top: position.y,
		left: position.x,
	};

	return (
		<div className="p-8 ta-center ta-c cursor-default">
			<Card
				onContextMenu={handleContextMenu}
				style={{ textAlign: 'center' }}
			>
				<BodyText>Right-click here to reveal context menu.</BodyText>
			</Card>
			<div style={styles}>
				<ActionMenu
					{...args}
					open={open}
					onClickOutside={close}
					onKeyDown={handleEscape}
				>
					<MenuItems />
				</ActionMenu>
			</div>
		</div>
	);
};
testContextMenu.args = {
	direction: 'br',
	width: 's',
};

export const testOverlayComponents = (args: any) => {
	const [menuOpen, setMenuOpen] = useState(true);

	const [modalOpen, setModalOpen] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [takeoverOpen, setTakeoverOpen] = useState(false);
	const [popoverOpen, setPopoverOpen] = useState(false);

	const [counter, setCounter] = useState(0);

	const overlays = [
		{ name: 'Modal', fn: () => setModalOpen(!modalOpen) },
		{ name: 'Drawer', fn: () => setDrawerOpen(!drawerOpen) },
		{ name: 'Dialog', fn: () => setDialogOpen(!dialogOpen) },
		{ name: 'Takeover', fn: () => setTakeoverOpen(!takeoverOpen) },
		{
			name: 'Popover',
			fn: () => setPopoverOpen(!popoverOpen),
			skipFromButtons: true,
		},
	];

	const CounterButton = () => (
		<Button
			className="m-x-1"
			iconName="project"
			onClick={() => {
				setCounter(counter + 1);
				console.log('counter:', counter);
			}}
		/>
	);

	const SecondaryMenu = () => (
		<Menu
			trigger={(props: any) => (
				<Button {...props}>
					Action Menu
					<Icon name="more_vert" size="24" className="m-l-2" />
				</Button>
			)}
		>
			{['1', '2', '3'].map((i, ix) => (
				<MenuItem key={ix}>Item {i}</MenuItem>
			))}
		</Menu>
	);

	return (
		<div>
			<BodyText className="m-y-2">
				ActionMenu to open Modal, Drawer, Dialog, Takeover, Popover,
				Tooltip:
			</BodyText>
			<Menu
				open={menuOpen}
				onClick={() => setMenuOpen(!menuOpen)}
				onClickOutside={() => setMenuOpen(false)}
				trigger={(props: any) => (
					<Button {...props}>
						Action Menu
						<Icon name="more_vert" size="24" className="m-l-2" />
					</Button>
				)}
				{...args}
			>
				{overlays.map((i, ix) => (
					<MenuItem onClick={i.fn} key={ix}>
						Open {i.name}
					</MenuItem>
				))}
				<MenuItem icon="help">
					<Tooltip text="Tooltip Text" el="div" direction="r">
						Show Tooltip
					</Tooltip>
				</MenuItem>
			</Menu>
			<BodyText className="m-y-2">
				ActionMenu placed inside Modal, Drawer, Dialog, Takeover,
				Popover:
			</BodyText>
			{overlays
				.filter((i) => i.skipFromButtons !== true)
				.map((i, ix) => (
					<div className="m-y-2" key={ix}>
						<Button onClick={i.fn}>Open {i.name}</Button>
					</div>
				))}
			<Modal
				title="Modal"
				open={modalOpen}
				closable
				onClose={() => overlays[0].fn()}
			>
				<SecondaryMenu />
				<CounterButton />
			</Modal>
			<Drawer
				header="Drawer"
				backdrop
				open={drawerOpen}
				onClose={() => overlays[1].fn()}
				footer={<Button onClick={overlays[1].fn}>Close Drawer</Button>}
			>
				<SecondaryMenu />
				<CounterButton />
			</Drawer>
			<Dialog
				title="Dialog"
				open={dialogOpen}
				onClose={() => overlays[2].fn()}
				onPrimaryActionClick={overlays[2].fn}
				primaryActionName="Close Dialog"
			>
				<SecondaryMenu />
				<CounterButton />
			</Dialog>
			{takeoverOpen && (
				<Takeover title="Takeover" onClose={() => overlays[3].fn()}>
					<SecondaryMenu />
					<CounterButton />
				</Takeover>
			)}
			<Popover
				header="Popover"
				open={popoverOpen}
				onClickOutside={() => (popoverOpen ? overlays[4].fn() : null)}
				portal
				trigger={<Button onClick={overlays[4].fn}>Open Popover</Button>}
				direction="tr"
			>
				<SecondaryMenu />
				<CounterButton />
			</Popover>
		</div>
	);
};
testOverlayComponents.args = {
	sharp: false,
	width: 's',
	direction: 'bl',
};

export const testActionMenuOverflow = (args: any) => {
	const [open, setOpen] = useState(true);
	const handleClick = () => setOpen(!open);
	const handleClickOutside = () => setOpen(false);

	return (
		<div style={{ width: '740px' }}>
			<Menu
				{...args}
				direction="br"
				width="xs"
				scrollHeight="256px"
				trigger={(p: any) => (
					<Button {...p} onClick={handleClick}>
						Action Menu
					</Button>
				)}
				open={open}
				onClickOutside={handleClickOutside}
			>
				<MenuItem>Item 1</MenuItem>
				<MenuItem disabled>Item 2 (disabled)</MenuItem>
				<MenuItem>Item 3</MenuItem>
				<MenuItem>Item 4</MenuItem>
				<MenuItem>Item 5</MenuItem>
				<Menu
					direction="rb"
					trigger={(p: any) => (
						<MenuItem {...p} cascading className="bg-blue-100">
							Submenu 1
						</MenuItem>
					)}
				>
					<ContactItems />
				</Menu>
				<MenuItem>Item 6</MenuItem>
			</Menu>
			{loremIpsum}
		</div>
	);
};

export const TestingMultipleActionMenu = () => {
	const [firstOpen, setFirstOpen] = useState(false);
	const [secondOpen, setSecondOpen] = useState(false);
	return (
		<>
			<div>
				<Stack
					alignItems="center"
					justifyContent="center"
					className="p-4"
					style={{ height: '1500px' }}
				>
					<ActionMenu
						open={firstOpen}
						direction="br"
						trigger={
							<Button onClick={() => setFirstOpen(!firstOpen)}>
								Element1
							</Button>
						}
					>
						aa
					</ActionMenu>
					<ActionMenu
						open={secondOpen}
						direction="br"
						trigger={
							<Button onClick={() => setSecondOpen(!secondOpen)}>
								Element2
							</Button>
						}
					>
						aa
					</ActionMenu>
				</Stack>
			</div>
		</>
	);
};

const loremIpsum = (
	<BodyText className="m-x-8 m-y-4 ta-left">
		Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
		tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
		veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
		commodo consequat. Duis aute irure dolor in reprehenderit in voluptate
		velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint
		occaecat cupidatat non proident, sunt in culpa qui officia deserunt
		mollit anim id est laborum. Lorem ipsum dolor sit amet, consectetur
		adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore
		magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
		laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor
		in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla
		pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa
		qui officia deserunt mollit anim id est laborum.
	</BodyText>
);
